import { CircularProgress } from '@material-ui/core';
import { Color, ColorDynamic, SuperDispatchTheme } from '@superdispatch/ui';
import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { trackLoadboardEvent } from '../LoadboardAnalytics';

const Wrapper = styled.div`
  cursor: pointer;
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img(
  ({ theme }: { theme: SuperDispatchTheme }) => css`
    object-fit: cover;
    border-radius: 3px;
    aspect-ratio: 4 / 3;

    transition: ${theme.transitions.create('width')};
  `,
);

const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${ColorDynamic.White};

  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionWrapper = styled.div(
  ({ theme }: { theme: SuperDispatchTheme }) => css`
    width: 24px;
    height: 24px;
    padding: 4px;
    position: absolute;

    border-radius: 50%;
    background-color: rgba(25, 35, 52, 0.65);
    transition: ${theme.transitions.create(['top', 'right'])};

    top: 4px;
    right: 4px;

    &[aria-expanded='true'] {
      top: 10px;
      right: 8px;
    }
  `,
);

interface ExpandIconProps {
  isExpanded?: boolean;
}

function ExpandIcon({ isExpanded }: ExpandIconProps) {
  return (
    <svg
      width="16"
      height="16"
      fill={Color.White}
      viewBox={!isExpanded ? '0 0 16 10' : '0 0 24 24'}
    >
      {isExpanded ? (
        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
      ) : (
        <path d="M8 0C4.66666 0 1.82 2.07333 0.666664 5C1.82 7.92667 4.66666 10 8 10C11.3333 10 14.18 7.92667 15.3333 5C14.18 2.07333 11.3333 0 8 0ZM8 8.33333C6.16 8.33333 4.66666 6.84 4.66666 5C4.66666 3.16 6.16 1.66667 8 1.66667C9.84 1.66667 11.3333 3.16 11.3333 5C11.3333 6.84 9.84 8.33333 8 8.33333ZM8 3C6.89333 3 6 3.89333 6 5C6 6.10667 6.89333 7 8 7C9.10666 7 10 6.10667 10 5C10 3.89333 9.10666 3 8 3Z" />
      )}
    </svg>
  );
}

interface Props {
  url: string;
}

export function VehicleImageViewer({ url }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [isLoaded, setLoadedState] = useState(false);
  const [isExpanded, setExpandedState] = useState(false);
  const fullWidth = ref.current?.parentElement
    ? ref.current.parentElement.clientWidth
    : 0;

  return (
    <Wrapper
      ref={ref}
      onClick={() => {
        setExpandedState(!isExpanded);
        trackLoadboardEvent({
          name: 'SLB: UI Interaction',
          type: isExpanded
            ? 'Vehicle list: Clicked Image Diminish'
            : 'Vehicle list: Clicked Image Enlarge',
        });
      }}
    >
      {isExpanded && !isLoaded && (
        <LoadingContainer>
          <CircularProgress size="26px" />
        </LoadingContainer>
      )}

      <Image
        alt=""
        src={url}
        width={isExpanded ? fullWidth : '83px'}
        onLoad={() => {
          setLoadedState(true);
        }}
      />

      <ActionWrapper aria-expanded={isExpanded}>
        <ExpandIcon isExpanded={isExpanded} />
      </ActionWrapper>
    </Wrapper>
  );
}
